import { useLazyQuery } from '@apollo/client/react';
import { CelloReferralCodeDocument } from '@graphql/queries/cello/referral-code.graphql';
import useNoRenderRef from '@hooks/use-no-render-ref';
import {
  CELLO_REFERRAL_FLAG,
  CELLO_REFERRAL_PROMOTION_CODE,
  getCachedCelloReferralCode,
  waitForCelloReferralVariant,
} from '@modules/cello-referral';
import { useFeatureFlag } from '@modules/feature-flag';
import { createChildLogger } from '@modules/logger';
import { useCallback } from 'react';

const logger = createChildLogger({ module: 'hooks/cello/use-cello-referral-eligibility' });

/**
 * The referral's contribution to a `useCheckout` call: the promotion code Stripe
 * discounts against, and the referral code stamped onto the subscription so the invoice
 * webhook can report the conversion and pay the referrer.
 */
export interface CelloReferralCheckoutArgs {
  promotionalCode: string;
  celloReferralCode: string;
}

export interface CelloReferralEligibility {
  /**
   * The cached referral code, or `null` when this user has no eligible referral.
   *
   * Not validated against the backend: the surfaces that route on this either validate
   * for themselves (the paywall) or are re-checked server-side (the signup report). A
   * caller that lets the code buy something must use `resolveReferralCheckoutArgs`.
   */
  resolveReferralCode: () => Promise<string | null>;

  /**
   * Checkout arguments carrying the referral discount, or `null` when the user has no
   * usable referral. Spread into a `useCheckout` call — the hook reads nothing from the
   * referral cache itself, so a purchase entry point that omits these charges the
   * referred user full price and reports no conversion (ACT-2224).
   *
   * Validates the code first, unlike the routing path: this is where money changes
   * hands.
   */
  resolveReferralCheckoutArgs: () => Promise<CelloReferralCheckoutArgs | null>;
}

/**
 * Whether the signed-in user has a referral that is still worth acting on.
 *
 * Deliberately free of routing so both the onboarding completion path and the
 * individual-plan purchase entry points — one of which is an App Router component —
 * can share one answer to "is this user referred, and are they in the experiment".
 */
export function useCelloReferralEligibility(): CelloReferralEligibility {
  // Read through perpetually up-to-date refs: the callbacks below are memoised and
  // reached from handlers that outlive the render they were created in, so capturing
  // the variant by closure would freeze it at the pre-fetch fallback ('off').
  const [variant, { loading }] = useFeatureFlag(CELLO_REFERRAL_FLAG, 'off', false);
  const variantRef = useNoRenderRef(variant);
  const flagLoadingRef = useNoRenderRef(loading);

  // Lazy rather than a mounted query: this resolves on a click, and every consumer of
  // this hook would otherwise validate on mount whether or not a purchase follows.
  const [validateReferralCode] = useLazyQuery(CelloReferralCodeDocument);

  const resolveReferralCode = useCallback(async (): Promise<string | null> => {
    const code = getCachedCelloReferralCode();

    if (!code) {
      return null;
    }

    await waitForCelloReferralVariant(flagLoadingRef);

    return variantRef.current === 'on' ? code : null;
  }, [flagLoadingRef, variantRef]);

  const resolveReferralCheckoutArgs =
    useCallback(async (): Promise<CelloReferralCheckoutArgs | null> => {
      const code = await resolveReferralCode();

      if (!code) {
        return null;
      }

      // Validated before it can buy anything. `createCheckoutSession` checks the Stripe
      // promotion code but never the Cello one, so an expired or unknown `ucc` left in
      // local storage would otherwise take 10% off the price and stamp the subscription
      // with a conversion nothing can attribute. The paywall route skips this because
      // the paywall validates before it offers; nothing downstream of here does.
      //
      // Fails closed, matching the paywall: a code that cannot be confirmed valid is
      // not a discount. The backend caches both verdicts in Redis, so this costs one
      // request rather than a Cello round trip.
      try {
        const { data, error } = await validateReferralCode({ variables: { code } });

        if (error || !data?.celloReferralCode.isValid) {
          logger.info('Not discounting checkout with an unusable Cello referral code', {
            validated: !error,
          });
          return null;
        }
      } catch (error) {
        logger.error('Could not validate the Cello referral code for checkout', { error });
        return null;
      }

      return { promotionalCode: CELLO_REFERRAL_PROMOTION_CODE, celloReferralCode: code };
    }, [resolveReferralCode, validateReferralCode]);

  return { resolveReferralCode, resolveReferralCheckoutArgs };
}
